import React, { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import LoadingStates from "@/components/LoadingStates";

const AppLayout: React.FC = () => {
	const { user, profile, loading } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		// AppLayout useEffect triggered

		if (loading) return; // Wait for auth to load

		// If user is not authenticated, redirect to login (except for public routes)
		if (!user) {
			const publicRoutes = [
				"/",
				"/login",
				"/signup",
				"/parent-signup",
				"/doula-signup",
			];
			if (!publicRoutes.includes(location.pathname)) {
				// No user, redirecting to login
				navigate("/login");
			}
			return;
		}

		// If user is authenticated, handle role-based routing
		if (user && profile) {
			const currentPath = location.pathname;
			// Authenticated user detected

			// Define route mappings for each role
			const routeMappings = {
				admin: {
					dashboard: "/admin-dashboard",
					allowedRoutes: [
						"/admin-dashboard",
						"/admin-users",
						"/admin-applications",
						"/admin-messaging",
						"/admin-faq",
					],
				},
				doula: {
					dashboard: "/doula-dashboard",
					onboarding: "/doula-onboarding",
					allowedRoutes: [
						"/doula-dashboard",
						"/doula-onboarding",
						"/doula-appointments",
						"/doula-messages",
						"/doula-earnings",
						"/doula-faq",
						"/doula-profile-management",
					],
				},
				parent: {
					dashboard: "/parent-dashboard",
					allowedRoutes: [
						"/parent-dashboard",
						"/parent-favorites",
						"/search-doulas",
						"/parent-messages",
						"/parent-bookings",
						"/parent-profile",
						"/parent-faq",
						"/doula-profile",
					],
				},
			};

			const userRole = profile.role;
			const mapping = routeMappings[userRole as keyof typeof routeMappings];

			if (!mapping) {
				// Unknown user role
				navigate("/");
				return;
			}

			// Role mapping found

			// Handle doula onboarding flow
			if (userRole === "doula" && !profile.onboarding_completed) {
				if (currentPath !== mapping.onboarding) {
					// Redirecting doula to onboarding
					navigate(mapping.onboarding);
					return;
				}
			}

			// If user is on a public route but should be on their dashboard
			const publicRoutes = [
				"/",
				"/login",
				"/signup",
				"/parent-signup",
				"/doula-signup",
			];
			if (publicRoutes.includes(currentPath)) {
				// User on public route, redirecting to dashboard
				navigate(mapping.dashboard);
				return;
			}

			// Check if user is on an allowed route for their role
			const isAllowedRoute = mapping.allowedRoutes.some((route) => {
				// Handle exact matches
				if (currentPath === route) return true;
				// Handle dynamic routes (e.g., /admin-applications/:id)
				if (route.includes("/:")) {
					const routePattern = route.replace(/\/:[^/]+/g, "/[^/]+");
					const regex = new RegExp(`^${routePattern}$`);
					return regex.test(currentPath);
				}
				// Handle dynamic admin application routes specifically
				if (
					route === "/admin-applications" &&
					currentPath.startsWith("/admin-applications/")
				) {
					return true;
				}
				return false;
			});

			if (!isAllowedRoute) {
				// User on unauthorized route, redirecting to dashboard
				navigate(mapping.dashboard);
				return;
			}

			// User on authorized route, no redirect needed
		} else if (user && !profile) {
			// User exists but no profile loaded yet
		}
	}, [user, profile, loading, location.pathname, navigate]);

	// Show loading while auth is being determined
	if (loading) {
		return <LoadingStates.PageLoading />;
	}

	return <Outlet />;
};

export default AppLayout;
